import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Business as BusinessIcon,
    PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import communityService from '../../services/communityService'; // Asegúrate de que este servicio existe
import userService from '../../services/userService'; // Asegúrate de que este servicio existe

const Communities = () => {
    const [communities, setCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estado para el diálogo de Comunidad
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCommunity, setEditingCommunity] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
    });

    // Estado para diálogo de Crear Admin
    const [openAdminDialog, setOpenAdminDialog] = useState(false);
    const [selectedCommunityForAdmin, setSelectedCommunityForAdmin] = useState(null);
    const [adminData, setAdminData] = useState({
        name: '',
        email: '',
        password: '',
    });

    useEffect(() => {
        fetchCommunities();
    }, []);

    const fetchCommunities = async () => {
        try {
            setLoading(true);
            const data = await communityService.getAll();
            setCommunities(data.data || []); // Asegurar que sea array
            setError(null);
        } catch (err) {
            console.error('Error:', err);
            // Mostrar mensaje más descriptivo
            setError(err.response?.data?.message || err.message || 'Error al cargar las comunidades');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (community = null) => {
        if (community) {
            setEditingCommunity(community);
            setFormData({
                name: community.name || '',
                address: community.address || '',
                email: community.admin_email || '',
            });
        } else {
            setEditingCommunity(null);
            setFormData({
                name: '',
                address: '',
                email: '',
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingCommunity(null);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCommunity) {
                await communityService.update(editingCommunity.id, formData);
            } else {
                await communityService.create(formData);
            }
            fetchCommunities();
            handleCloseDialog();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Error al guardar');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar esta comunidad? Esta acción no se puede deshacer.')) {
            try {
                await communityService.delete(id);
                fetchCommunities();
            } catch (err) {
                console.error(err);
                alert(err.response?.data?.message || 'Error al eliminar');
            }
        }
    };

    // Handlers para Crear Admin
    const handleOpenAdminDialog = (community) => {
        setSelectedCommunityForAdmin(community);
        setAdminData({ name: '', email: '', password: '' });
        setOpenAdminDialog(true);
    };

    const handleCloseAdminDialog = () => {
        setOpenAdminDialog(false);
        setSelectedCommunityForAdmin(null);
    };

    const handleAdminChange = (e) => {
        setAdminData({
            ...adminData,
            [e.target.name]: e.target.value,
        });
    };

    const handleAdminSubmit = async (e) => {
        e.preventDefault();
        try {
            // Llamada al endpoint de crear usuario, pasando community_id
            await userService.create({
                ...adminData,
                role: 'admin',
                community_id: selectedCommunityForAdmin.id,
            });
            alert('Administrador creado exitosamente');
            handleCloseAdminDialog();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Error al crear administrador');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="large" color="primary" />
                    Comunidades
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Nueva Comunidad
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Nombre</TableCell>
                                <TableCell>Dirección</TableCell>
                                <TableCell>Teléfono</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {communities.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">No hay comunidades registradas</TableCell>
                                </TableRow>
                            ) : (
                                communities.map((comm) => (
                                    <TableRow key={comm.id}>
                                        <TableCell>{comm.id}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{comm.name}</TableCell>
                                        <TableCell>{comm.address || '-'}</TableCell>
                                        <TableCell>{comm.phone || '-'}</TableCell>
                                        <TableCell>{comm.email || '-'}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Crear Admin">
                                                <IconButton color="success" onClick={() => handleOpenAdminDialog(comm)}>
                                                    <PersonAddIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar">
                                                <IconButton color="primary" onClick={() => handleOpenDialog(comm)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Eliminar">
                                                <IconButton color="error" onClick={() => handleDelete(comm.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Diálogo de Crear/Editar Comunidad */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCommunity ? 'Editar Comunidad' : 'Nueva Comunidad'}
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            name="name"
                            label="Nombre de la Comunidad"
                            type="text"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="address"
                            label="Dirección"
                            type="text"
                            fullWidth
                            multiline
                            rows={2}
                            value={formData.address}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="phone"
                            label="Teléfono"
                            type="tel"
                            fullWidth
                            value={formData.phone}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="email"
                            label="Correo Electrónico"
                            type="email"
                            fullWidth
                            value={formData.email}
                            onChange={handleChange}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancelar</Button>
                        <Button type="submit" variant="contained">Guardar</Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Diálogo Crear Admin */}
            <Dialog open={openAdminDialog} onClose={handleCloseAdminDialog} maxWidth="xs" fullWidth>
                <DialogTitle>
                    Crear Admin para {selectedCommunityForAdmin?.name}
                </DialogTitle>
                <form onSubmit={handleAdminSubmit}>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            name="name"
                            label="Nombre del Administrador"
                            type="text"
                            fullWidth
                            required
                            value={adminData.name}
                            onChange={handleAdminChange}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="email"
                            label="Correo Electrónico"
                            type="email"
                            fullWidth
                            required
                            value={adminData.email}
                            onChange={handleAdminChange}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="password"
                            label="Contraseña"
                            type="password"
                            fullWidth
                            required
                            value={adminData.password}
                            onChange={handleAdminChange}
                            helperText="Mínimo 6 caracteres"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseAdminDialog}>Cancelar</Button>
                        <Button type="submit" variant="contained" color="success">Crear Admin</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default Communities;
